import React, { useEffect, useState } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Box, Button, Checkbox, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { DragIndicator, Edit, Delete, Visibility, VisibilityOff } from '@mui/icons-material';
import parse from 'html-react-parser';
import { neutral, primary } from '_lex/_foundations/phxColors';
import ContentEditor from 'components/ContentEditor';
import { translate as t } from 'hooks/useTranslations';

export interface ComplianceChecklistItemProps {
  id: string;
  /** Raw HTML content */
  content: string;
  completed?: boolean;
  enabled: boolean;
  isEditing: boolean;
  onStartEdit: () => void;
  /** Called with the saved HTML when the user saves via the ContentEditor toolbar */
  onSaveEdit: (contentHTML: string) => void;
  onCancelEdit: () => void;
  /** When false, the edit icon is hidden and content cannot be modified (Lexipol-published items) */
  canEditContent?: boolean;
  onToggleCompleted?: () => void;
  onToggleEnabled: () => void;
  onDelete?: () => void;
  deleteDisabled?: boolean;
  deleteDisabledTooltip?: string;
  /** True when any mutation in the list is in-flight */
  busy: boolean;
  /** True specifically when this item's save is in-flight */
  isSaving: boolean;
}

const ComplianceChecklistItem: React.FC<ComplianceChecklistItemProps> = ({
  id,
  content,
  completed,
  enabled,
  isEditing,
  onStartEdit,
  onSaveEdit,
  onCancelEdit,
  canEditContent = true,
  onToggleCompleted,
  onToggleEnabled,
  onDelete,
  deleteDisabled = false,
  deleteDisabledTooltip,
  busy,
  isSaving,
}) => {
  const [pendingHTML, setPendingHTML] = useState(content);

  useEffect(() => {
    if (isEditing) setPendingHTML(content);
  }, [isEditing]);

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
    disabled: isEditing,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 1 : undefined,
  };

  if (isEditing) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
        }}
      >
      <Box
        sx={{
          position: 'relative',
          zIndex: 1,
          border: `1px solid ${primary[800]}`,
          borderRadius: 1,
          bgcolor: 'background.paper',
          mb: 0.5,
        }}
      >
        <ContentEditor
          id={`compliance-item-edit-${id}`}
          contentHTML={content}
          isEditable
          variant='minimal'
          onSave={setPendingHTML}
          trackChangesEnabled={false}
          textEntitiesEnabled={false}
          crossReferencesEnabled={false}
          imagesEnabled={false}
          attachmentsEnabled={false}
          excalidrawEnabled={false}
        />
        </Box>
        <Stack direction='row' justifyContent='flex-end' alignItems='center' gap={1}>
          <Button variant='text' color='primary' size='small' onClick={onCancelEdit} disabled={busy || isSaving}>
            {t('awb.complianceChecklist.cancel')}
          </Button>
          <Button
            variant='contained'
            color='primary'
            size='small'
            onClick={() => onSaveEdit(pendingHTML)}
            disabled={busy || isSaving}
            loading={isSaving}
            loadingPosition='end'
          >
            {t('awb.complianceChecklist.save')}
          </Button>
        </Stack>
      </Box>
    );
  }

  return (
    <Stack
      ref={setNodeRef}
      style={style}
      direction='row'
      alignItems='flex-start'
      sx={{
        bgcolor: enabled ? 'background.paper' : neutral[50],
        borderBottom: `1px solid ${neutral[100]}`,
        py: 1,
        pr: 0.5,
        boxShadow: isDragging
          ? '0px 4px 8px rgba(75,85,101,0.1), 0px 2px 4px rgba(75,85,101,0.06)'
          : 'none',
        '&:hover': { bgcolor: enabled ? neutral[25] : neutral[50] },
      }}
    >
      {/* Drag handle */}
      <Box
        {...attributes}
        {...listeners}
        sx={{
          display: 'flex',
          alignItems: 'center',
          px: 0.5,
          pt: 0.5,
          color: neutral[300],
          cursor: isDragging ? 'grabbing' : 'grab',
          touchAction: 'none',
        }}
      >
        <DragIndicator fontSize='small' />
      </Box>

      {/* Completed checkbox */}
      {onToggleCompleted && (
        <Tooltip
          title={completed ? t('awb.complianceChecklist.uncheckItem') : t('awb.complianceChecklist.checkItem')}
          placement='top'
        >
          <span>
            <Checkbox
              checked={completed ?? false}
              onChange={onToggleCompleted}
              disabled={busy}
              size='small'
              sx={{ p: 0.5 }}
              aria-label={completed ? t('awb.complianceChecklist.uncheckItem') : t('awb.complianceChecklist.checkItem')}
            />
          </span>
        </Tooltip>
      )}

      {/* Content */}
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          px: 1,
          py: 0.5,
          textDecoration: enabled ? 'none' : 'line-through',
          color: enabled ? 'text.primary' : 'text.disabled',
          '& p': { my: 0.25, fontSize: 14 },
          '& ul, & ol': { pl: 2 },
        }}
      >
        {content ? (
          parse(content)
        ) : (
          <Typography variant='bodySm' color='text.disabled'>
            {t('awb.complianceChecklist.lexipolTemplate.noDescription')}
          </Typography>
        )}
      </Box>

      {/* Actions */}
      <Stack direction='row' alignItems='center' spacing={0} sx={{ flexShrink: 0, pt: 0.25 }}>
        {canEditContent && (
          <Tooltip title={t('awb.complianceChecklist.editItem')} placement='top'>
            <span>
              <IconButton size='small' onClick={onStartEdit} disabled={busy} aria-label={t('awb.complianceChecklist.editItem')}>
                <Edit sx={{ fontSize: 16, color: neutral[500] }} />
              </IconButton>
            </span>
          </Tooltip>
        )}
        <Tooltip
          title={
            enabled
              ? t('awb.complianceChecklist.lexipolTemplate.disableItem')
              : t('awb.complianceChecklist.lexipolTemplate.enableItem')
          }
          placement='top'
        >
          <span>
            <IconButton
              size='small'
              onClick={onToggleEnabled}
              disabled={busy}
              aria-label={
                enabled
                  ? t('awb.complianceChecklist.lexipolTemplate.disableItem')
                  : t('awb.complianceChecklist.lexipolTemplate.enableItem')
              }
            >
              {enabled ? (
                <Visibility sx={{ fontSize: 16, color: neutral[500] }} />
              ) : (
                <VisibilityOff sx={{ fontSize: 16, color: neutral[400] }} />
              )}
            </IconButton>
          </span>
        </Tooltip>
        {onDelete && (
          <Tooltip
            title={
              deleteDisabled && deleteDisabledTooltip
                ? deleteDisabledTooltip
                : t('awb.complianceChecklist.deleteItem')
            }
            placement='top'
          >
            <span>
              <IconButton
                size='small'
                onClick={onDelete}
                disabled={busy || deleteDisabled}
                aria-label={
                  deleteDisabled && deleteDisabledTooltip
                    ? deleteDisabledTooltip
                    : t('awb.complianceChecklist.deleteItem')
                }
              >
                <Delete
                  sx={{
                    fontSize: 16,
                    color: deleteDisabled ? neutral[300] : neutral[500],
                  }}
                />
              </IconButton>
            </span>
          </Tooltip>
        )}
      </Stack>
    </Stack>
  );
};

export default ComplianceChecklistItem;
